// Render PDF pages to raster images (PNG) fully in the renderer via pdf.js.
import { loadPdfDocument } from "./loadDocument";

export interface ExportedImage {
  name: string;
  blob: Blob;
  pageNumber: number;
}

/**
 * Render the given 1-based page numbers (or all) to PNG at `scale`.
 * scale 2 ≈ 144 DPI, good default for screen/share.
 */
export const exportPagesToPng = async (
  bytes: Uint8Array,
  baseName: string,
  pageNumbers?: number[],
  scale = 2,
): Promise<ExportedImage[]> => {
  const doc = await loadPdfDocument(bytes);
  const pages = pageNumbers ?? Array.from({ length: doc.numPages }, (_, i) => i + 1);
  const out: ExportedImage[] = [];

  // pdf.js can stall rendering to a fully detached canvas, so render into one
  // attached offscreen, then remove it.
  const holder = document.createElement("div");
  holder.style.cssText =
    "position:fixed;left:0;top:0;opacity:0;pointer-events:none;z-index:-1;";
  document.body.appendChild(holder);
  try {
    for (const n of pages) {
      const page = await doc.getPage(n);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;
      holder.appendChild(canvas);
      await page.render({ canvasContext: ctx, viewport }).promise;
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      holder.removeChild(canvas);
      if (blob) out.push({ name: `${baseName}_p${n}.png`, blob, pageNumber: n });
    }
  } finally {
    document.body.removeChild(holder);
  }
  return out;
};
